import { useMemo } from "react";

import useVariant from "./useVariant";
import { sortVariants } from "helpers/variant";

import type { Variant } from "types/app";
import type useDistinctVariantTypes from "./useDistinctVariantTypes";

type MappedVariantData = Record<string, Variant>;

/**
 * Only use this hook under DataContext
 *
 * To be used with the return value of `useDistinctVariantTypes` hook
 *
 * @param baseVariant - the variant to match the groups against, the closest to this is returned for that group.
 * It is basically the variant which will be selected out of the group if that code is selected
 */
export default function useMapToClosestVariant(
  // TODO: `useDistinctVariantTypes` is imported as type & still `typeof` needs to be used
  availabilityData: ReturnType<typeof useDistinctVariantTypes>,
  baseVariant?: Variant,
  preferPriorityBasedSort = false,
): MappedVariantData {
  const selectedVariant = useVariant();
  const variant = baseVariant || selectedVariant;

  return useMemo(() => {
    if (!variant) return {};

    return availabilityData.reduce((mapData, { code, group }) => {
      if (group.length === 1) mapData[code] = group[0];
      else {
        const sortedList = sortVariants(
          group,
          {
            ...variant,

            /* unset below unimportant factors */
            id: undefined,
            price: undefined,
            motorPower: undefined,
            motorTorque: undefined,
          },
          preferPriorityBasedSort,
        );

        mapData[code] = sortedList[0];
      }

      return mapData;
    }, {} as MappedVariantData);
  }, [variant, availabilityData, preferPriorityBasedSort]);
}
